import { ReactNode, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import useRoot from '../../../../1_application/core/roots/rootController';
import { ReactComponent as Down } from '../../../../assets/icons/arrowDown.svg';
import { LXbody } from '../../texts/lxText';
import Column from '../box/column';
import Row from '../box/row';
import SizedBox from '../box/sizedBox';
import LXMiddleButton from './lxMiddleButton';
import LXSmallButton from './lxSmallButton';

interface LXSelectorProps<T> {
  values: T[];
  labels: string[];
  value?: T;
  onSelect: (data: T) => void;
  placeholder?: string;
  dropdownDirection?: 'top' | 'bottom';
  tabIndex?: number;
  kind?: 'normal' | 'secondary' | 'custom' | 'small';
  customHeader?: ReactNode;
}

interface DropdownHeaderContainerProps {
  dropdownHeaderWidth?: number;
}

interface DropdownItemContainerProps {
  dropdownItemWidth?: number;
  top?: number;
  left?: number;
  bottom?: number;
  borderColor?: string;
  backgroundColor?: string;
}

const DropdownHeaderContainer = styled.div<DropdownHeaderContainerProps>`
  width: ${(props) => `${props.dropdownHeaderWidth}px`};
  border: ${(props) => `1px solid ${props.theme.colors.grayLine}`};
  padding: 11px 11px 11px 15px;
  background-color: ${(props) => props.theme.colors.white};
  border-radius: 12px;
  position: relative;
  cursor: pointer;
`;

const DropdownItemContainer = styled.div<DropdownItemContainerProps>`
  width: ${(props) => `${props.dropdownItemWidth}px`};
  border: ${(props) => `1px solid ${props.borderColor}`};
  background-color: ${(props) => props.backgroundColor};
  padding: 11px 0px;
  position: absolute;
  border-radius: 12px;
  top: ${(props) => `${props.top}px`};
  left: ${(props) => `${props.left}px`};
  bottom: ${(props) => `${props.bottom}px`};
  z-index: 999999;
`;

interface DropdownItemProps {
  isSelect: boolean;
}

const DropdownItem = styled.div<DropdownItemProps>`
  font-family: 'Outfit';
  font-style: normal;
  height: 34px;
  font-weight: 400;
  font-size: 14px;
  color: ${(props) => (props.isSelect ? '#B32651' : '#555555')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 8px 24px;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: #fff4f7;
    color: #b32651;
  }
`;

const DropdownBackground = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  z-index: 999998;
`;

const LXSelector = <T,>(
  props: LXSelectorProps<T> &
    DropdownHeaderContainerProps &
    DropdownItemContainerProps
) => {
  const theme = useTheme();
  const ref = useRef<any>();
  const { render, clearAllChildren } = useRoot();

  const onDropdownClickHandler = () => {
    const rect = ref.current.getBoundingClientRect();
    const scrollY = window.scrollY;
    // warning 뜨는거 추후에 처리

    render(
      'lxSelectorContainer',
      <DropdownItemContainer
        dropdownItemWidth={props.dropdownItemWidth}
        borderColor={theme.colors.grayLine}
        backgroundColor={theme.colors.white}
        bottom={
          props.dropdownDirection === 'top'
            ? -(rect.y - 8 + scrollY)
            : undefined
        }
        top={
          props.dropdownDirection === 'top'
            ? undefined
            : rect.bottom + 8 + scrollY
        }
        left={rect.x}
      >
        <LXDropDown {...props} removeItems={clearAllChildren}></LXDropDown>
      </DropdownItemContainer>
    );

    render(
      'lxSelectorBackgroundContainer',
      <DropdownBackground onClick={clearAllChildren}></DropdownBackground>
    );
  };

  const findLabelByValue = () => {
    if (props.value === undefined) {
      return undefined;
    } else {
      return props.labels[props.values.indexOf(props.value)];
    }
  };

  if (props.kind === undefined || props.kind === 'normal') {
    return (
      <DropdownHeaderContainer
        dropdownHeaderWidth={props.dropdownHeaderWidth}
        onClick={(e) => {
          onDropdownClickHandler();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onDropdownClickHandler();
          }
        }}
        ref={ref}
        tabIndex={props.tabIndex}
      >
        <Row justifyContent="space-between" alignItems="center">
          <LXbody
            color={props.value ? theme.colors.body : theme.colors.placeHolder}
          >
            {findLabelByValue() || props.placeholder}
          </LXbody>
          <Down></Down>
        </Row>
      </DropdownHeaderContainer>
    );
  } else if (props.kind === 'custom' && props.customHeader !== undefined) {
    return (
      <div ref={ref} onClick={async () => onDropdownClickHandler()}>
        {props.customHeader}
      </div>
    );
  } else if (props.kind === 'small') {
    return (
      <div ref={ref}>
        <LXSmallButton
          onClick={async () => onDropdownClickHandler()}
          kind={'secondary'}
        >
          {props.placeholder}
        </LXSmallButton>
      </div>
    );
  } else {
    return (
      <div ref={ref}>
        <LXMiddleButton
          onClick={async () => onDropdownClickHandler()}
          kind={'secondary'}
        >
          {props.placeholder}
        </LXMiddleButton>
      </div>
    );
  }
};

interface LXDropDownProps {
  removeItems: () => void;
}

const LXDropDown = <T,>(props: LXSelectorProps<T> & LXDropDownProps) => {
  const buildItems = () => {
    return props.labels.map((label, idx) => (
      <DropdownItem
        tabIndex={
          props.tabIndex !== undefined ? props.tabIndex + 1 + idx : undefined
        }
        key={idx}
        isSelect={props.value === label}
        onClick={() => {
          props.onSelect(props.values[idx]);
          props.removeItems();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            props.onSelect(props.values[idx]);
            props.removeItems();
          }
        }}
      >
        {label}
      </DropdownItem>
    ));
  };
  return <Column>{buildItems()}</Column>;
};

export default LXSelector;

interface LXLabelSelectorProps {
  label: string;
}

export const LXLabelSelector = <T,>(
  props: LXLabelSelectorProps &
    LXSelectorProps<T> &
    DropdownHeaderContainerProps &
    DropdownItemContainerProps
) => {
  const theme = useTheme();

  return (
    <Column>
      <LXbody color={theme.colors.title}>{props.label}</LXbody>
      <SizedBox height={8}></SizedBox>
      <LXSelector {...props}></LXSelector>
    </Column>
  );
};
